import { createSlice } from '@reduxjs/toolkit';
import {
  CancelUploadAction,
  CreateVisitAction,
  CreateVisitSuccessAction,
  GetEDTFSchemasSuccessAction,
  GetPolingStatusAction,
  GetPolingStatusCheckAction,
  GetPolingStatusSuccessAction,
  SetStatusAction,
  SiteUploadState,
  SubmitUploadAction,
  UpdateEDTFSchemasAction,
  UpdateEDTFDataAction,
  UploadFileAction,
  UploadFileSuccessAction,
  UploadStatus,
} from './siteUpload.types';
import { createSliceHook, makeActionCreator, createSelector } from '../helpers';
import {
  StatusCodes,
  UploadFormData,
} from 'src/components/SiteUserUpload/types/uploadTypes';
import { UploadFileTypeEnum } from '@dynamika-shared/types/inventory';

const initialState: SiteUploadState = {
  data: null,
  loading: false,
  selectedSchema: null,
  schemas: [],
  edtfData: null,
  signedUrlResponse: null,
  status: UploadStatus.INITIAL_VIEW,
  polingData: null,
  shouldContinuePolling: false,
  fileType: null,
  isReUpload: false,
  schemasLoading: true,
};

const { act, actReq, actResolve, actReject, actEmpty } =
  makeActionCreator<SiteUploadState>();

export const siteUploadSlice = createSlice({
  name: 'siteUpload',
  initialState,
  reducers: {
    createVisit: actReq<CreateVisitAction['payload']>(),
    createVisitSuccess: act<CreateVisitSuccessAction['payload']>(
      ({ data }) => ({
        data,
        loading: false,
      })
    ),
    createVisitFailure: actReject(),

    setFileType: act<UploadFileTypeEnum | null>(fileType => ({
      fileType,
    })),
    uploadFile: act<UploadFileAction['payload']>(() => ({
      status: UploadStatus.FILE_UPLOAD_IN_PROGRESS,
      loading: true,
    })),
    setShouldPolling: act<boolean>(shouldContinuePolling => ({
      shouldContinuePolling,
    })),
    uploadFileSuccess: act<UploadFileSuccessAction['payload']>((s, action) => ({
      status: action.status,
      shouldContinuePolling: action.shouldContinuePolling,
    })),
    uploadFileFailure: act<Error>(error => ({
      error,
      status: UploadStatus.FILE_UPLOAD_ERROR,
    })),

    getPolingStatus: actEmpty<GetPolingStatusAction['payload']>(),
    getPolingStatusCheck: actResolve<GetPolingStatusCheckAction['payload']>(
      'polingData',
      null,
      null
    ),
    getPolingStatusSuccess: act<GetPolingStatusSuccessAction['payload']>(
      ({ status, pollingData }) => ({ status, pollingData })
    ),
    getPolingStatusFailure: act<Error>(error => ({
      error,
      status: UploadStatus.FILE_POLING_ERROR,
    })),

    finalizeUpload: act<null>(() => ({
      status: UploadStatus.FILE_FINALIZE_INIT,
      loading: true,
    })),
    finalizeUploadSuccess: actEmpty<StatusCodes>(),
    finalizeUploadFailure: act<null>(() => ({
      status: UploadStatus.FILE_FINALIZE_ERROR,
      shouldContinuePolling: false,
    })),

    setReupload: act<void>(() => ({
      loading: true,
      signedUrlResponse: null,
      status: UploadStatus.INITIAL_VIEW,
      polingData: null,
      error: undefined,
      isReUpload: true,
    })),
    setReuploadSuccess: act<void>(() => ({
      isReUpload: false,
    })),
    setReuploadFailure: actReject(),

    submitUpload: actReq<SubmitUploadAction['payload']>(),
    submitUploadSuccess: state => {
      state.status = UploadStatus.FINALIZE_UPLOAD;
    },
    submitUploadFailure: actReject(),

    updateEdtfData: actResolve<UpdateEDTFDataAction['payload']>('edtfData'),

    updateEdtfDataFailure: actReject(),

    cancelUpload: actReq<CancelUploadAction['payload']>(),
    cancelUploadSuccess: state => {
      state.status = UploadStatus.INITIAL_VIEW;
      state.shouldContinuePolling = false;
    },
    cancelUploadFailure: actReject(),

    getEDTFSchemas: actReq<void>('schemasLoading'),
    getEDTFSchemasSuccess:
      actResolve<GetEDTFSchemasSuccessAction['payload']>('schemas', 'schemasLoading'),
    getEDTFSchemasFailure: actReject('schemasError', 'schemasLoading'),

    clearData: act<void>(() => ({
      ...initialState,
    })),
    setStatus: actResolve<SetStatusAction['payload']>('status', null, null),
    clearError: state => {
      state.error = undefined;
    },
    selectEDTFSchema: act<UploadFormData>(selectedSchema => ({
      selectedSchema,
    })),
    updateEDTFSchemas:
      actResolve<UpdateEDTFSchemasAction['payload']>('loading'),
    setSignedUrlResponse: actResolve('signedUrlResponse'),
    setIsReUploadStatus: act<boolean>(isReUpload => ({
      isReUpload,
    })),
  },
});

export const actions = {
  ...siteUploadSlice.actions,
};

export const useSiteUploadSlice = createSliceHook(
  siteUploadSlice.name,
  actions
);

export const selectSiteUploadState = createSelector('siteUpload');
